import React from 'react';
import { merge } from 'lodash';
import { FinanceType } from '@upstart/harmony/lib/legos/constants';
import ProdigyInput from '../../../GlobalUI/ProdigyInput';
import {
  TieredAprConfigOptions,
  PurchasePricingOptions,
  PurchasePricingValues,
} from '../types';
import {
  ORDERED_PURCHASE_TERMS,
  ORDERED_PURCHASE_TERM_TO_LABEL,
} from '../constants';
import {
  AprConfigValidationResult,
  getValidationKey,
} from '../useAprConfigValidator';

function TiersPurchase({
  purchase,
  options,
  validationResult,
  onTiersPurchasePartialUpdate,
}: {
  purchase: PurchasePricingOptions;
  options: TieredAprConfigOptions;
  validationResult: AprConfigValidationResult;
  onTiersPurchasePartialUpdate: (
    value: Partial<PurchasePricingOptions>,
  ) => void;
}) {
  const renderEditablePurchaseTableField = (
    key: keyof PurchasePricingValues,
    label: string,
  ) =>
    purchase?.pricing.map((values, tierIndex, pricingMatrix) => {
      const tierNumber = tierIndex + 1;
      return (
        <div
          key={`finance-tier-${tierNumber}-markup`}
          className="flex flex-col space-y-15"
        >
          <div className="flex flex-row space-x-30">
            <div className="flex w-100px prodigy-input-container">
              <label className="label">
                {`Tier ${tierIndex + 1} ${label}`}
              </label>
            </div>
            {values.map((_, termIndex) => (
              <ProdigyInput
                value={values[termIndex][key]}
                disabled={!purchase.terms[termIndex]}
                className={
                  [
                    'w-full',
                    'flex-grow',
                    validationResult?.errors?.[
                      getValidationKey({
                        financeType: FinanceType.PURCHASE,
                        tierIndex,
                        termIndex,
                        suffix: key,
                      })
                    ]
                      ? 'tier-error'
                      : '',
                  ].join(' ') as never
                }
                onChangeHandler={(e: string) => {
                  const strippedValue = e.replace(/[^0-9.]/g, '') ?? '';
                  onTiersPurchasePartialUpdate(
                    merge(purchase, {
                      pricing: [
                        ...pricingMatrix.slice(0, tierIndex),
                        [
                          ...values.slice(0, termIndex),
                          {
                            ...values[termIndex],
                            [key]: strippedValue,
                          },
                          ...values.slice(termIndex + 1),
                        ],
                        ...pricingMatrix.slice(tierIndex + 1),
                      ],
                    }),
                  );
                }}
              />
            ))}
          </div>
        </div>
      );
    });

  return (
    <>
      <legend>Finance APR Settings</legend>
      <div className="flex flex-col space-y-30">
        <div className="w-full">
          <div className="flex flex-row space-x-30">
            <div className="flex w-100px prodigy-input-container">
              <label className="label">Terms (Months)</label>
            </div>
            {ORDERED_PURCHASE_TERMS.map((key, idx) => (
              <ProdigyInput
                value={purchase?.terms?.[idx]}
                label={`Finance ${ORDERED_PURCHASE_TERM_TO_LABEL[key]}`}
                className={
                  [
                    'w-full',
                    'flex-grow',
                    validationResult?.errors?.[
                      getValidationKey({
                        financeType: FinanceType.PURCHASE,
                        termIndex: idx,
                        suffix: 'months',
                      })
                    ]
                      ? 'tier-error'
                      : '',
                  ].join(' ') as never
                }
                onChangeHandler={(e: string) => {
                  const value = e.replace(/[^0-9]/g, '');
                  onTiersPurchasePartialUpdate(
                    merge(purchase, {
                      terms: [
                        ...purchase.terms.slice(0, idx),
                        value,
                        ...purchase.terms.slice(idx + 1),
                      ],
                    }),
                  );
                }}
              />
            ))}
          </div>
        </div>
        <div className="w-full">
          {renderEditablePurchaseTableField('apr', 'APR %')}
          {options.addOEMMarkupRate &&
            renderEditablePurchaseTableField('markup', 'Markup')}
        </div>
      </div>
    </>
  );
}

export default TiersPurchase;
